import React, { useMemo, useState } from 'react';

import { Flex, Table } from 'antd-v5';
import { debounce, orderBy } from 'lodash';
import { useSelector } from 'react-redux';
import { Search } from 'scouting-ui/lib/Input';

import { canViewYouthAdvancementDetailsSel } from '@modules/roster-lite/duck/selectors';
import type { UnitRosterMember } from '@modules/roster-lite/models/unitRosterMember';
import { isMobileSel } from '@shared/duck/selectors/general.selectors';
import { intl } from '@shared/localization';

import styles from './UnitRosterTable.module.scss';
import { getColumns } from './columns';

interface UnitRosterTableProps {
  guid: string;
  members: UnitRosterMember[];
  loading?: boolean;
}

export default function UnitRosterTable(props: UnitRosterTableProps) {
  const { guid, members, loading } = props;
  const isMobile = useSelector(isMobileSel);

  const [searchQuery, setSearchQuery] = useState('');
  const canOpenYouthProfile = useSelector(canViewYouthAdvancementDetailsSel);

  const handleSearch = useMemo(
    () => debounce((value: string) => setSearchQuery(value), 300),
    [],
  );

  const filteredMembers = useMemo(() => {
    const q = searchQuery.trim().toLowerCase();
    const base = q
      ? members.filter(m => {
          const haystack = [m.name, m.member_id, ...m.positions]
            .join('\0')
            .toLowerCase();
          return haystack.includes(q);
        })
      : members;
    return orderBy(base, ['name']);
  }, [members, searchQuery]);

  const positionFilters = useMemo(() => {
    const unique = Array.from(new Set(members.flatMap(m => m.positions))).sort(
      (a, b) => a.localeCompare(b),
    );
    return unique.map(p => ({ text: p, value: p }));
  }, [members]);

  const subUnitFilters = useMemo(() => {
    const unique = Array.from(
      new Set(members.map(m => m.sub_unit).filter((s): s is string => !!s)),
    ).sort((a, b) => a.localeCompare(b));
    return unique.map(s => ({ text: s, value: s }));
  }, [members]);

  const columns = useMemo(
    () =>
      getColumns(
        guid,
        positionFilters,
        subUnitFilters,
        canOpenYouthProfile,
        isMobile,
      ),
    [canOpenYouthProfile, guid, isMobile, positionFilters, subUnitFilters],
  );

  return (
    <Flex vertical gap={0}>
      <div className={styles.searchContainer}>
        <Search
          placeholder={intl.formatMessage({
            id: 'unitRoster.table.searchPlaceholder',
          })}
          disabled={loading}
          onChange={e => handleSearch(e.target.value)}
          allowClear
          onClear={() => setSearchQuery('')}
        />
      </div>
      <Table<UnitRosterMember>
        size="small"
        rowKey="member_id"
        columns={columns}
        dataSource={filteredMembers}
        loading={loading}
        sticky={{ offsetHeader: 60 + 48 + 8 * 2 }}
        scroll={{ x: 'max-content' }}
        pagination={{ defaultPageSize: 20 }}
      />
    </Flex>
  );
}
